import { useGetPatientFeatureEntitlementQuery } from '../../services/entitlements/operations.generated';
import { RoomForPatient } from '../../services/room';
import { UserSubscriptionType } from '../../services/subscriptions';
import { useService } from '../useService';
import { FronteggFeatureKeys } from '../../context/userEntitlementsContext.types';
import { getIsGraphqlRoomQueriesEnabled } from '../../utils/isGraphqlRoomQueriesEnabled';

type UsePatientPaidStatusReturn = {
  isPaidUser: boolean;
  roomOwnerSubscriptionType: UserSubscriptionType;
  freeOrPaid: 'free' | 'paid';
};

export const usePatientPaidStatus = (
  room?: RoomForPatient | null,
): UsePatientPaidStatusReturn => {
  const isProvider = useService('$context') === 'provider';
  const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();
  const hasOwner = Boolean(room?.owner);

  // These do not run once native room queries are enabled
  const { currentData: legacyIsPaidEntitlement } =
    useGetPatientFeatureEntitlementQuery(
      {
        input: {
          featureKey: FronteggFeatureKeys.LegacyIsPaid,
          roomSlug: room?.slug as string,
        },
      },
      {
        skip: !room?.slug || isGraphqlRoomQueriesEnabled || isProvider,
      },
    );

  const { currentData: patientProEntitlement } =
    useGetPatientFeatureEntitlementQuery(
      {
        input: {
          featureKey: FronteggFeatureKeys.ProPlan,
          roomSlug: room?.slug as string,
        },
      },
      {
        skip: !room?.slug || isGraphqlRoomQueriesEnabled || isProvider,
      },
    );

  const { currentData: patientPremiumEntitlement } =
    useGetPatientFeatureEntitlementQuery(
      {
        input: {
          featureKey: FronteggFeatureKeys.PremiumPlan,
          roomSlug: room?.slug as string,
        },
      },
      {
        skip: !room?.slug || isGraphqlRoomQueriesEnabled || isProvider,
      },
    );

  if (isProvider || !hasOwner) {
    return {
      isPaidUser: false,
      roomOwnerSubscriptionType: UserSubscriptionType.Free,
      freeOrPaid: 'free',
    };
  }

  const legacyIsPaid =
    legacyIsPaidEntitlement?.Entitlements.getPatientFeatureEntitlement
      ?.isEntitled;
  const hasPro =
    patientProEntitlement?.Entitlements.getPatientFeatureEntitlement
      ?.isEntitled;
  const hasPremium =
    patientPremiumEntitlement?.Entitlements.getPatientFeatureEntitlement
      ?.isEntitled;

  const isPaidUser = isGraphqlRoomQueriesEnabled
    ? room?.owner?.subscriptionType !== UserSubscriptionType.Free
    : Boolean(legacyIsPaid);

  // Plan precedence: Premium > Pro > Free
  // If both Pro and Premium features are enabled (unexpected during normal operation),
  // Premium takes precedence as the higher-tier plan
  let roomOwnerSubscriptionType = UserSubscriptionType.Free;

  if (isGraphqlRoomQueriesEnabled && room?.owner?.subscriptionType) {
    roomOwnerSubscriptionType = room?.owner?.subscriptionType;
  } else {
    if (hasPremium) {
      roomOwnerSubscriptionType = UserSubscriptionType.Premium;
    } else if (hasPro) {
      roomOwnerSubscriptionType = UserSubscriptionType.Pro;
    }
  }

  return {
    isPaidUser,
    roomOwnerSubscriptionType,
    freeOrPaid: isPaidUser ? 'paid' : 'free',
  };
};
